import { all, call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import * as actions from './actions';
import * as actionsNotification from '../InitComponent/actions';
import { LoyaltyService } from './service';
import {
  ReceiptsResponseData,
  LoyaltyPageState,
  AddLoyaltyProductPayload,
  LoyaltyProductsResponse,
  LoyaltyPercentagesResponse,
  RecommendationsResponse,
  CreditBonusesPayload,
  SendToStandartPayload,
  GetHealthStatusPayload,
  GetClientOrdersData,
  CurrentReceiptData,
} from './types';
import { validationLoyaltyCodeErrorHandler } from '../LoginPage/helpers';
import { formatReceipt } from './helpers';
import {
  CANNOT_CHANGE_PAYMENT_METHOD,
  NOTIFICATION_CHANGES_NOT_SAVED,
  NOTIFICATION_REPORT_ERROR,
  NOTIFICATION_SKU_NOT_FOUND,
} from '../../global/constants';
import { formatAxiosErrorDetails } from '../../global/helpers';
import { OrdersService } from '../OrdersPage/service';
import { PaymentsResponseCache } from '../OrdersPage/types';

interface GetClientBalanceSagaProps {
  type: string;
  payload: any;
}

interface GetCurrentReceiptSagaProps {
  type: string;
  payload: {
    postEffect?: (res: CurrentReceiptData[]) => void;
  };
}

interface GetClientOrdersSagaProps {
  type: string;
  payload: {
    phone: string;
    postEffect?: (res: GetClientOrdersData) => void;
  };
}

interface AapplyBonusesSagaProps {
  type: string;
  payload: string;
}

interface SubmitLoyaltyOrderSagaProps {
  type: string;
  payload: {
    phone: string;
    bonus_used: boolean;
    doc_id: number;
    source_code: string;
    items: { were_id: string; price: number; quantity: number; name: string }[];
    client_bonus_debit: number;
    client_bonus_credit: number;
    pharmacist_bonus_credit: number;
    isPartner: boolean;
    confirm_code?: string;
    postEffect?: () => void;
  };
}

interface SetPaymentMethodSagaProps {
  type: string;
  payload: { orderId: string; postEffect?: any };
}

interface GetPartnerPaymentsSagaProps {
  type: string;
  payload: string;
}

interface SendToStandartSagaProps {
  type: string;
  payload: SendToStandartPayload;
}

interface GetHealthStatusSagaProps {
  type: string;
  payload: GetHealthStatusPayload;
}

interface GenerateLoyaltyReportParam {
  type: string;
  payload: {
    data: {
      from: string;
      to: string;
      integration_code: string;
      type: string;
      source_code?: string;
      phone?: string;
    };
    pdf: boolean;
    xls: boolean;
    postEffect: any;
  };
}

interface AddLoyaltyProductSagaProps {
  type: string;
  payload: AddLoyaltyProductPayload;
}

interface DeleteLoyaltyProductSagaProps {
  type: string;
  payload: { sku: string };
}

interface GetRecommendationsSagaProps {
  type: string;
  payload: {
    user_hash: string;
    source_code: string;
    user_cart: Array<{
      ware_id: string;
      name: string;
      quantity: number;
    }>;
  };
}

interface CreditBonusesSagaProps {
  type: string;
  payload: CreditBonusesPayload;
}

interface SetLoyaltyInfoBot {
  type: string;
  payload: {
    sourceCode: string;
    action: string;
    networkName: string;
    dockId: string;
    postEffect: any;
  };
}

export function* getCurrentReceiptSaga({
  payload,
}: GetCurrentReceiptSagaProps) {
  try {
    const res: ReceiptsResponseData = yield call(
      LoyaltyService.getCurrentReceipt
    );
    const formattedReceipt = formatReceipt(res);

    if (payload?.postEffect) {
      payload.postEffect(formattedReceipt?.checks);
    }
    yield put(actions.getCurrentReceipt.success(formattedReceipt));
  } catch (error) {
    const isTimeout =
      axios.isAxiosError(error) &&
      (error.code === 'ECONNABORTED' ||
        error.message?.toLowerCase().includes('timeout'));

    const messageErr = {
      message: isTimeout ? 'RECEIPT_TIMEOUT' : 'RECEIPT_ERROR',
    };

    yield put(actions.getCurrentReceipt.error(messageErr));
  }
}

export function* getClientInfoSaga({ payload }: GetClientBalanceSagaProps) {
  try {
    const res: {
      result: LoyaltyPageState['clientInfo']['result'];
      status: string;
      pharmacy?: { phone: string };
    } = yield call(LoyaltyService.getClientInfo, payload);
    if (payload.postEffect) {
      payload.postEffect(res?.pharmacy?.phone ?? '');
    }
    yield put(actions.getClientInfo.success(res));
  } catch (error) {
    yield put(actions.getClientInfo.error(error));
  }
}

export function* getPharmacistTodayBonusesSaga() {
  try {
    const res: { result: number; status: string } = yield call(
      LoyaltyService.getPharmacistTodayBonuses
    );
    yield put(actions.getPharmacistTodayBonuses.success(res?.result ?? 0));
  } catch (error) {
    yield put(actions.getPharmacistTodayBonuses.error(error));
  }
}

export function* getClientOrdersSaga({ payload }: GetClientOrdersSagaProps) {
  try {
    const res: {
      status: string;
      result: GetClientOrdersData;
    } = yield call(LoyaltyService.getClientOrders, payload);

    if (payload.postEffect) {
      payload.postEffect(res.result);
    }
    yield put(actions.getClientOrders.success(res.result));
  } catch (error) {
    const { response } = error as {
      response: {
        data: {
          error: string;
        };
        status: number;
      };
    };
    yield put(
      actions.getClientOrders.error(
        validationLoyaltyCodeErrorHandler(response?.data?.error)
      )
    );
  }
}

export function* applyBonusesSaga({ payload }: AapplyBonusesSagaProps) {
  try {
    const res: {
      status: string;
    } = yield call(LoyaltyService.applyBonuses, payload);
    yield put(actions.applyBonuses.success(res?.status));
  } catch (error) {
    const { response } = error as {
      response: {
        data: {
          error: string;
        };
        status: number;
      };
    };

    // Передаём статус и оригинальный текст ошибки для обработки в компоненте
    const errorMessage = `${response.status}:${
      response.data.error || 'Unknown error'
    }`;
    yield put(actions.applyBonuses.error(errorMessage));
  }
}

export function* submitLoyaltyOrderSaga({
  payload,
}: SubmitLoyaltyOrderSagaProps) {
  try {
    const res: {
      status: string;
      docId: string;
    } = yield call(LoyaltyService.submitLoyaltyOrder, payload);
    if (payload.postEffect) {
      payload.postEffect();
    }
    yield put(actions.submitLoyaltyOrder.success(res));
  } catch (error) {
    const { response } = error as {
      response: {
        data: {
          error: string;
        };
        status: number;
      };
    };

    yield put(
      actions.submitLoyaltyOrder.error(
        validationLoyaltyCodeErrorHandler(response.data.error)
      )
    );
  }
}

export function* setPartnerPaymentMethodSaga({
  payload,
}: SetPaymentMethodSagaProps) {
  try {
    const res: {
      status: string;
      docId: string;
    } = yield call(LoyaltyService.setPartnerPaymentMethod, payload.orderId);

    if (payload.postEffect) {
      payload.postEffect();
    }
    yield put(actions.setPartnerPaymentMethod.success(res));
  } catch (error) {
    const { response } = error as {
      response: {
        data: {
          error: string;
        };
        status: number;
      };
    };
    yield put(
      actionsNotification.addApplicationNotification({
        content: CANNOT_CHANGE_PAYMENT_METHOD,
        canBeClosed: true,
        type: 'error',
      })
    );
    yield put(
      actions.setPartnerPaymentMethod.error(
        validationLoyaltyCodeErrorHandler(response.data.error)
      )
    );
  }
}

export function* setInPlacePaymentMethodSaga({
  payload,
}: SetPaymentMethodSagaProps) {
  try {
    const res: {
      status: string;
      docId: string;
    } = yield call(LoyaltyService.setInPlacePaymentMethod, payload.orderId);

    if (payload.postEffect) {
      payload.postEffect();
    }
    yield put(actions.setInPlacePaymentMethod.success(res));
  } catch (error) {
    const { response } = error as {
      response: {
        data: {
          error: string;
        };
        status: number;
      };
    };
    yield put(
      actionsNotification.addApplicationNotification({
        content: CANNOT_CHANGE_PAYMENT_METHOD,
        canBeClosed: true,
        type: 'error',
      })
    );
    yield put(
      actions.setInPlacePaymentMethod.error(
        validationLoyaltyCodeErrorHandler(response.data.error)
      )
    );
  }
}

export function* getPartnerPaymentsSaga({
  payload,
}: GetPartnerPaymentsSagaProps) {
  try {
    const response: PaymentsResponseCache = yield call(
      OrdersService.getOrderPaymentsById,
      String(payload)
    );

    yield put(actions.getPartnerPayments.success(response?.result));
  } catch (error) {
    yield put(actions.getPartnerPayments.error(error));
  }
}

export function* sendToStandartSaga({ payload }: SendToStandartSagaProps) {
  try {
    const res: { status: string } = yield call(
      LoyaltyService.sendToStandart,
      payload
    );

    if (payload.postEffect) {
      yield call(payload.postEffect);
    }
    yield put(actions.sendToStandart.success(res));
  } catch (error) {
    if (payload.postEffect) {
      yield call(payload.postEffect);
    }
    yield put(actions.sendToStandart.error(error));
  }
}

export function* getHealthStatusSaga({ payload }: GetHealthStatusSagaProps) {
  try {
    const res: { status: string } = yield call(
      LoyaltyService.getHealthStatus,
      payload
    );
    yield put(actions.getHealthStatus.success(res));
  } catch (error) {
    yield put(actions.getHealthStatus.error(error));
  }
}

export function* generateLoyaltyReportSaga({
  payload,
}: GenerateLoyaltyReportParam) {
  try {
    const response: { status: string; result: string } = yield call(
      LoyaltyService.generateLoyaltyReport,
      payload.data,
      payload.pdf,
      payload.xls
    );

    if (payload.postEffect) {
      yield call(payload.postEffect);
    }
    yield put(actions.generateLoyaltyReport.success(response.result));
  } catch (error) {
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_REPORT_ERROR,
        canBeClosed: true,
        type: 'error',
      })
    );
    yield put(actions.generateLoyaltyReport.error(error));
  }
}

export function* addLoyaltyProductSaga({
  payload,
}: AddLoyaltyProductSagaProps) {
  try {
    const response: { status: string } = yield call(
      LoyaltyService.addLoyaltyProduct,
      payload
    );
    yield put(actions.addLoyaltyProduct.success(response.status));
    // Показываем успешное уведомление
    yield put(
      actionsNotification.addApplicationNotification({
        content: 'Повышенный бонус добавлен!',
        canBeClosed: true,
        type: 'success',
      })
    );
    // обновляем список товаров
    yield put(actions.getLoyaltyProducts.request());
  } catch (error) {
    const errText = (error as any).response.data.error || '';

    yield put(
      actionsNotification.addApplicationNotification({
        content:
          errText === 'sku not found'
            ? NOTIFICATION_SKU_NOT_FOUND
            : NOTIFICATION_CHANGES_NOT_SAVED,
        canBeClosed: true,
        type: 'error',
      })
    );
    yield put(actions.addLoyaltyProduct.error(error));
  }
}

export function* getLoyaltyProductsSaga() {
  try {
    const response: LoyaltyProductsResponse = yield call(
      LoyaltyService.getLoyaltyProducts
    );
    yield put(actions.getLoyaltyProducts.success(response));
  } catch (error) {
    yield put(actions.getLoyaltyProducts.error(error));
  }
}

export function* deleteLoyaltyProductSaga({
  payload,
}: DeleteLoyaltyProductSagaProps) {
  try {
    const response: { status: string } = yield call(
      LoyaltyService.deleteLoyaltyProduct,
      payload
    );
    yield put(actions.deleteLoyaltyProduct.success(response.status));
    // обновляем список товаров
    yield put(actions.getLoyaltyProducts.request());
  } catch (error) {
    yield put(actions.deleteLoyaltyProduct.error(error));
  }
}

export function* getLoyaltyPercentagesSaga() {
  try {
    const response: LoyaltyPercentagesResponse = yield call(
      LoyaltyService.getLoyaltyPercentages
    );
    yield put(actions.getLoyaltyPercentages.success(response));
  } catch (error) {
    yield put(actions.getLoyaltyPercentages.error(error));
  }
}

// Saga для получения рекомендаций
export function* getRecommendationsSaga({
  payload,
}: GetRecommendationsSagaProps) {
  try {
    const response: RecommendationsResponse = yield call(
      LoyaltyService.getRecommendations,
      payload
    );
    yield put(actions.getRecommendations.success(response));
  } catch (error) {
    // Обрабатываем ошибку, но не показываем модальное окно для внешнего API
    yield put(
      actions.getRecommendations.error('Не удалось загрузить рекомендации')
    );
  }
}

export function* creditBonusesSaga({ payload }: CreditBonusesSagaProps) {
  try {
    const response: { status: string } = yield call(
      LoyaltyService.creditBonuses,
      payload
    );
    yield put(actions.creditBonuses.success(response));
    // Показываем успешное уведомление
    yield put(
      actionsNotification.addApplicationNotification({
        content: 'Бонусы начислены успешно!',
        canBeClosed: true,
        type: 'success',
      })
    );
  } catch (error) {
    const { response } = error as {
      response?: {
        data?: {
          error?: string;
          message?: string;
        };
      };
      message?: string;
    };
    const errorMessage =
      response?.data?.error ||
      response?.data?.message ||
      (error as any)?.message ||
      'Неизвестная ошибка';
    yield put(actions.creditBonuses.error(errorMessage));
    // ошибка при начислении бонусов
    yield put(
      actionsNotification.addApplicationNotification({
        content: `Бонусы НЕ начислены. Ошибка: ${errorMessage}`,
        canBeClosed: true,
        type: 'error',
      })
    );
  }
}

export function* setLoyaltyInfoBotSaga({ payload }: SetLoyaltyInfoBot) {
  try {
    const response: { result: { text: string } } = yield call(
      LoyaltyService.setLoyaltyInfoBot,
      payload.sourceCode,
      payload.action,
      payload.networkName,
      payload.dockId
    );

    if (payload.postEffect) {
      payload.postEffect();
    }
    yield put(actions.setLoyaltyInfoBot.success(response.result));
  } catch (error) {
    const errorDetails = formatAxiosErrorDetails(error);
    console.error('Ошибка отправки уведомления в бот:', errorDetails);
    yield put(actions.setLoyaltyInfoBot.error(errorDetails));
  }
}

export function* loyaltyPageWatcherSaga() {
  yield all([
    takeLatest(actions.getCurrentReceipt.REQUEST, getCurrentReceiptSaga),
    takeLatest(actions.getClientInfo.REQUEST, getClientInfoSaga),
    takeLatest(
      actions.getPharmacistTodayBonuses.REQUEST,
      getPharmacistTodayBonusesSaga
    ),
    takeLatest(actions.getClientOrders.REQUEST, getClientOrdersSaga),
    takeLatest(actions.applyBonuses.REQUEST, applyBonusesSaga),
    takeLatest(actions.submitLoyaltyOrder.REQUEST, submitLoyaltyOrderSaga),
    takeLatest(
      actions.setPartnerPaymentMethod.REQUEST,
      setPartnerPaymentMethodSaga
    ),
    takeLatest(
      actions.setInPlacePaymentMethod.REQUEST,
      setInPlacePaymentMethodSaga
    ),
    takeLatest(actions.getPartnerPayments.REQUEST, getPartnerPaymentsSaga),
    takeLatest(actions.sendToStandart.REQUEST, sendToStandartSaga),
    takeLatest(actions.getHealthStatus.REQUEST, getHealthStatusSaga),
    takeLatest(
      actions.generateLoyaltyReport.REQUEST,
      generateLoyaltyReportSaga
    ),
    takeLatest(actions.addLoyaltyProduct.REQUEST, addLoyaltyProductSaga),
    takeLatest(actions.getLoyaltyProducts.REQUEST, getLoyaltyProductsSaga),
    takeLatest(actions.deleteLoyaltyProduct.REQUEST, deleteLoyaltyProductSaga),
    takeLatest(
      actions.getLoyaltyPercentages.REQUEST,
      getLoyaltyPercentagesSaga
    ),
    takeLatest(actions.getRecommendations.REQUEST, getRecommendationsSaga),
    takeLatest(actions.creditBonuses.REQUEST, creditBonusesSaga),
    takeLatest(actions.setLoyaltyInfoBot.REQUEST, setLoyaltyInfoBotSaga),
  ]);
}
